/**
 * Page Router
 * Auto-discovers routes using Vite glob imports and file-based conventions
 */

import React, { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';

// Auto-discover all page files using Vite glob import (including nested) - eager loading
const pageFiles = import.meta.glob('../features/*/pages/**/*.{tsx,jsx}', { eager: true });

function generateRoutes() {
  console.log('🔍 [Router] Starting route discovery...');
  console.log('🔍 [Router] Found', Object.keys(pageFiles).length, 'page files');

  const routes: Array<{ path: string; component: React.ComponentType<any> }> = [];

  // Process each discovered page file
  Object.entries(pageFiles).forEach(([filePath, module]) => {
    console.log('📄 [Router] Processing file:', filePath);

    // Extract feature and nested path from file path
    // Examples:
    // ../features/main/pages/index.tsx -> feature: main, nested: ['index']
    // ../features/gallery/pages/new/cat.tsx -> feature: gallery, nested: ['new', 'cat']
    // ../features/gallery/pages/[animal].tsx -> feature: gallery, nested: ['[animal]']
    // ../features/blog/pages/[...slug].tsx -> feature: blog, nested: ['[...slug]']
    const match = filePath.match(/\.\.\/features\/([^/]+)\/pages\/(.+)\.tsx?$/);

    if (!match) {
      console.log('⚠️  [Router] No match for file pattern:', filePath);
      return;
    }

    const [, feature, nestedPath] = match;
    const pathSegments = nestedPath.split('/');

    console.log('  ➜ Feature:', feature);
    console.log('  ➜ Nested path:', nestedPath);
    console.log('  ➜ Path segments:', pathSegments);

    // Convention-based routing logic
    let routePath: string;

    if (feature === 'main') {
      // Main feature gets priority routes
      if (pathSegments.length === 1 && pathSegments[0] === 'index') {
        routePath = '/';
      } else {
        // Convert nested path: about/details -> /about/details
        routePath = '/' + pathSegments
          .map(segment => segment === 'index' ? '' : segment.toLowerCase())
          .filter(Boolean)
          .map(segment => {
            // Handle catch-all routes: [...slug] -> *
            if (segment.startsWith('[...') && segment.endsWith(']')) {
              return '*';
            }
            // Handle dynamic params: [animal] -> :animal
            if (segment.startsWith('[') && segment.endsWith(']')) {
              return ':' + segment.slice(1, -1);
            }
            return segment;
          })
          .join('/');
      }
    } else {
      // Other features: /feature/nested/path
      const nestedRoute = pathSegments
        .map(segment => segment === 'index' ? '' : segment.toLowerCase())
        .filter(Boolean)
        .map(segment => {
          // Handle catch-all routes: [...slug] -> *
          if (segment.startsWith('[...') && segment.endsWith(']')) {
            return '*';
          }
          // Handle dynamic params: [animal] -> :animal
          if (segment.startsWith('[') && segment.endsWith(']')) {
            return ':' + segment.slice(1, -1);
          }
          return segment;
        })
        .join('/');

      if (pathSegments.length === 1 && pathSegments[0] === 'index') {
        routePath = `/${feature}`;
      } else {
        routePath = `/${feature}${nestedRoute ? '/' + nestedRoute : ''}`;
      }
    }

    console.log('  ✅ [Router] Mapped to route:', routePath);

    routes.push({
      path: routePath,
      component: (module as any).default
    });
  });

  console.log('\n📋 [Router] Routes before sorting:', routes.map(r => r.path));

  // Sort routes so more specific ones come first
  routes.sort((a, b) => {
    // Root route should be last for proper matching
    if (a.path === '/') return 1;
    if (b.path === '/') return -1;

    // Static routes should come before dynamic routes (params or wildcards)
    const aHasDynamic = a.path.includes(':') || a.path.includes('*');
    const bHasDynamic = b.path.includes(':') || b.path.includes('*');

    console.log(`🔄 [Router] Comparing routes:
      Route A: ${a.path} (dynamic: ${aHasDynamic})
      Route B: ${b.path} (dynamic: ${bHasDynamic})`);

    if (aHasDynamic && !bHasDynamic) {
      console.log('  ➜ B comes first (B is static, A is dynamic)');
      return 1; // b comes first
    }
    if (!aHasDynamic && bHasDynamic) {
      console.log('  ➜ A comes first (A is static, B is dynamic)');
      return -1; // a comes first
    }

    // If both static or both dynamic, sort by length (longer = more specific)
    const result = b.path.length - a.path.length;
    console.log(`  ➜ Both same type, sorting by length: ${result > 0 ? 'B' : 'A'} comes first (${b.path.length} vs ${a.path.length})`);
    return result;
  });

  console.log('\n🚀 [Router] Final sorted routes:');
  routes.forEach((route, index) => {
    const isDynamic = route.path.includes(':') || route.path.includes('*');
    console.log(`  ${index + 1}. ${route.path} ${isDynamic ? '(dynamic)' : '(static)'}`);
  });

  return routes;
}

// Component to handle scroll to top on route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    console.log('🧭 [Router] Navigation detected:', pathname);
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

export const PageRouter: React.FC = () => {
  const routes = generateRoutes();

  return (
    <>
      <ScrollToTop />
      <Routes>
        {routes.map(({ path, component: Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}
        {/* Fallback for 404 */}
        <Route path="*" element={
          <div className="text-center py-12 text-muted-foreground">
            <h1 className="text-4xl font-bold mb-4">404</h1>
            <p>Page not found - Route not discovered</p>
            <div className="mt-4 text-sm">
              Available routes: {routes.map(r => r.path).join(', ')}
            </div>
          </div>
        } />
      </Routes>
    </>
  );
};